import React, { useState, useEffect } from 'react'
import './index.css'
import { Contract, CurrencyList, ContractPerformance } from '../../types'
import { Structure } from '../../../Org/Structure/types'

import HoverTooltip from '../../../../../components/HoverTooltip'

type Props = {
    keyindex: number
    stage: string
    performance: ContractPerformance
    structData: Structure[]
    selected: boolean
    onClick: (guid: string) => void
    onDoubleClick: (guid: string, contract_reg_number: string) => void
    onRightClick: (guid: string) => void
    theme: string
    currency: CurrencyList[]
}

const CONTRACT_STATUS = new Map([
    ['F', 'Редактирование'],
    ['P', 'Размещено'],
    ['I', 'Недействительно'],
    ['M', 'Изменение'],
])

const CONTRACT_DRAFT_STATUS = new Map([
    ['P', 'Размещение'],
    ['A', 'Аннулирование позиции'],
    ['N', 'Новая позиция'],
    ['C', 'Изменение позиции'],
])

const MONTHS = [
    'январь',
    'февраль',
    'март',
    'апрель',
    'май',
    'июнь',
    'июль',
    'август',
    'сентябрь',
    'октябрь',
    'ноябрь',
    'декабрь',
]

const GWS = new Map([
    ['G', 'Товар'],
    ['W', 'Работа'],
    ['S', 'Услуга'],
])

const PerformanceContractsTableRow: React.FC<Props> = ({
    keyindex,
    stage,
    performance,
    structData,
    selected,
    onClick,
    onDoubleClick,
    onRightClick,
    theme,
    currency,
}) => {
    const getNameStruct = (struct_guid: string) => {
        const nameSctruct = structData.find((s) => s.guid === struct_guid)
        return nameSctruct?.struct_short_name || '-'
    }

    const updatedDate = new Date(performance.sys_updated_at)
    updatedDate.setHours(updatedDate.getHours())

    const formattedUpdateDate = updatedDate
        .toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        })
        .replace(/\//g, '.')
    const formattedUpdateTime = updatedDate.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })

    const createdDate = new Date(performance.sys_created_at)
    createdDate.setHours(createdDate.getHours())

    const STATUS_COLOR =
        theme == 'dark'
            ? new Map([
                  ['Черновик', '#8d79ff'],
                  ['Готово', '#049664'],
                  ['Подготовка', '#745cff'],
                  ['Готово к отправке', '#563af5'],
                  ['Отправлено в ЕИС', '#049664'],
                  ['Размещена', '#636366'],
                  ['Аннулирована', '#333033'],
              ])
            : new Map([
                  ['Черновик', '#dfddff'],
                  ['Готово', '#83d2b7'],
                  ['Подготовка', '#d3cfff'],
                  ['Готово к отправке', '#b7acff'],
                  ['Отправлено в ЕИС', '#83d2b7'],
                  ['Размещена', '#cfd6e2'],
                  ['Аннулирована', '#F6FBFF'],
              ])

    const STAGE_COLOR =
        theme == 'dark'
            ? new Map([
                  ['Редактирование', '#745cff'],
                  ['Подготовка к размещению', '#563af5'],
                  ['Отправка в ЕИС', '#4406be'],
                  ['Размещенное исполнение', '#636366'],
              ])
            : new Map([
                  ['Редактирование', '#d3cfff'],
                  ['Подготовка к размещению', '#a199dd'],
                  ['Отправка в ЕИС', '#a191ff'],
                  ['Размещенное исполнение', '#cfd6e2'],
              ])

    return (
        <div
            className={
                selected
                    ? 'plan-position-table-row-selected'
                    : 'plan-position-table-row'
            }
            onDoubleClick={() =>
                onDoubleClick(performance.guid, performance.contract_reg_number)
            }
            onClick={() => onClick(performance.guid)}
            onContextMenu={(e) => {
                e.preventDefault() // Отключает стандартное контекстное меню
                onRightClick(performance.guid)
            }}
        >
            <div className="flex h-[70px] w-8 items-center justify-center text-center">
                <input
                    type="checkbox"
                    checked={selected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => {
                        onClick(performance.guid)
                    }}
                />
            </div>
            <div className="flex h-[70px] w-12 items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                {keyindex}
            </div>
            <div className="flex h-[70px] w-20 items-center justify-center text-center">
                <HoverTooltip
                    tooltipText={
                        performance.contract_name
                            ? performance.contract_name
                            : '-'
                    }
                >
                    <span className="fourrow">
                        {performance.contract_name
                            ? performance.contract_name.length > 10
                                ? performance.contract_name.slice(0, 10) + '...'
                                : performance.contract_name
                            : '-'}
                    </span>
                </HoverTooltip>
            </div>
            <div
                className={`flex h-[70px] w-24 p-1 items-center justify-center text-center rounded`}
                style={{
                    backgroundColor: performance.performance_stage
                        ? STAGE_COLOR.get(performance.performance_stage)
                        : 'transparent',
                }}
            >
                {performance.performance_stage}
            </div>
            <div
                className="flex h-[70px] w-24 p-1 items-center justify-center text-center rounded"
                style={{
                    backgroundColor: performance.performance_status
                        ? STATUS_COLOR.get(performance.performance_status)
                        : 'transparent',
                }}
            >
                {performance.performance_status}
            </div>

            <div className="flex h-[70px] flex-1 items-center justify-start text-left min-w-44 px-2 ">
                <HoverTooltip tooltipText={performance.contract_lot_info}>
                    <p className="fourrow">
                        {performance.contract_lot_info
                            ? performance.contract_lot_info
                            : '-'}
                    </p>
                </HoverTooltip>
            </div>

            {/* <div className="flex h-[70px] w-28 items-center justify-end text-right px-2 gap-1">
                <HoverTooltip
                    tooltipText={
                        performance.currency_digital_code
                            ? String(
                                  currency.find(
                                      (c) =>
                                          c.num_code ===
                                          String(performance.currency_digital_code)
                                  )?.name
                              )
                            : '-'
                    }
                >
                    <span>
                        {performance.currency_digital_code
                            ? performance.currency_digital_code == 643
                                ? ''
                                : currency.find(
                                      (c) =>
                                          c.num_code ===
                                          String(performance.currency_digital_code)
                                  )?.char_sign || '?'
                            : ''}{' '}
                        {Number(performance.price).toLocaleString('ru-RU', {
                            minimumFractionDigits: 2,
                        })}
                    </span>
                </HoverTooltip>
            </div> */}
            <div className="flex flex-col h-[70px] w-44 items-center justify-center text-center">
                <HoverTooltip tooltipText={performance.contract_supplier_name}>
                    <p className="tworow">
                        {performance.contract_supplier_name}
                    </p>
                </HoverTooltip>
            </div>
            <div className="flex h-[70px] w-28 items-center justify-end text-right px-2 ">
                {performance.contract_contract_date
                    ?.split('-')
                    .reverse()
                    .join('-')
                    .replace(/[\-]/g, '.')}
            </div>
            <div className="flex h-[70px] w-28 items-center justify-end text-right px-2 ">
                {performance.completion_stage_end_date
                    ?.split('-')
                    .reverse()
                    .join('-')
                    .replace(/[\-]/g, '.')}
            </div>
            <div className="flex h-[70px] w-28 items-center justify-end text-right px-2 ">
                {Number(performance.total).toLocaleString('ru-RU', {
                    minimumFractionDigits: 2,
                })}
            </div>
            <div className="flex h-[70px] w-24 items-center justify-center text-center">
                {performance.type_object_contract
                    ? GWS.get(String(performance.type_object_contract))
                    : '-'}
            </div>
            <HoverTooltip
                tooltipText={`${performance.struct_name ? performance.struct_name : 'Отсутствует'}`}
                align="center"
            >
                <div className="flex h-[70px] w-24 items-center justify-center text-center">
                    {performance.struct_guid !== undefined &&
                    performance.struct_guid !== null &&
                    performance.struct_guid !== ''
                        ? getNameStruct(performance.struct_guid)
                        : '-'}
                </div>
            </HoverTooltip>
            <div className="flex h-[70px] w-40 items-center justify-center text-center">
                {performance.registration_number}
            </div>
            <div className="flex h-[70px] w-20 items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                {formattedUpdateDate}
                <br />
                {formattedUpdateTime}
            </div>
            <div className="flex h-[70px] w-14 items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                <HoverTooltip
                    tooltipText={
                        performance.first_name !== null
                            ? performance.last_name +
                              ' ' +
                              performance.first_name
                            : 'Отсутствует'
                    }
                    align="left"
                >
                    <div className="flex h-8 w-8 rounded-full items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d9dce0] dark:bg-[#333033]">
                        {performance.first_name !== null
                            ? performance.last_name?.charAt(0) +
                              performance.first_name?.charAt(0)
                            : 'О'}
                    </div>
                </HoverTooltip>
            </div>
        </div>
    )
}

export default PerformanceContractsTableRow
